/**
 * Wrappers that supply exam data from URL slug context to existing exam/practice pages.
 * Used under routes like /:lang/exams/:examSlug/...
 */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useExamFromSlug } from '../layout/ExamSlugRoute';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { auth } from '../../firebase';
import examService from '../../services/examService';
import enhancedUserService from '../../services/enhancedUserService';
import { getExamTitle, getExamH1, getLessonTitle, getLessonH1 } from '../../utils/examSeo';
import ExamDetailPage from '../practice/ExamDetailPage';
import ExamInstructionsPage from '../practice/ExamInstructionsPage';
import ExamSimulatorPage from '../practice/ExamSimulatorPage';
import ExamResultsPage from '../practice/ExamResultsPage';
import RetryPage from '../results/RetryPage';
import PracticePage from '../../pages/PracticePage';
import VocabularyPage from '../vocabulary/VocabularyPage';
import GrammarPage from '../grammar/GrammarPage';
import RedemittelPage from '../../pages/RedemittelPage';
import ExamSectionPractice from '../practice/ExamSectionPractice';
import LesenTrainingPage from '../../pages/LesenTrainingPage';
import HorenTrainingPage from '../../pages/HorenTrainingPage';
import SchreibenTrainingPage from '../../pages/SchreibenTrainingPage';
import StudyPlanPage from '../study/StudyPlanPage';
import GrammarLearningContainer from '../grammar/GrammarLearningContainer';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import storageService from '../../services/storageService';
import { examPapers } from '../../data/mockExamPapers';
import { PAPER_LEVELS } from '../paperExam/paperLevels';
import {
  examSupportsPaper, paperSlugFor, getExamModePreference, setExamModePreference, loadExamModePreference,
} from '../../utils/examMode';

// The paper exam (booklet view) loads only when someone sits one.
const TelcPaperSitting = lazy(() => import('../paperExam/telc/TelcPaperSitting.jsx'));

function useExamSlugNav() {
  const navigate = useLocalizedNavigate();
  const { lang } = useParams();
  const { examSlug } = useExamFromSlug() || {};
  const base = `/exams/${examSlug || ''}`;
  return {
    navigate,
    toExam: () => navigate(base),
    toInstructions: () => navigate(`${base}/instructions`),
    toSimulator: () => navigate(`${base}/simulator`),
    toResults: () => navigate(`${base}/results`),
    toPractice: () => navigate(`${base}/practice`),
    toPracticeGrammar: () => navigate(`${base}/practice/grammar`),
    toPracticeVocab: () => navigate(`${base}/practice/vocabulary`),
    toPracticeRedemittel: () => navigate(`${base}/practice/redemittel`),
    toLessons: () => navigate(`${base}/lessons`),
    toDashboard: () => navigate('/dashboard'),
  };
}

export function ExamSlugDetailPage() {
  const { lang } = useParams();
  const ctx = useOutletContext() || {};
  const { examData, examSlug } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  const navigate = useLocalizedNavigate();
  useEffect(() => {
    if (examSlug && lang) document.title = getExamTitle(examSlug, lang, 'hub');
  }, [examSlug, lang]);
  if (!examData) return null;
  const seoH1 = examSlug && lang ? getExamH1(examSlug, lang, 'hub') : '';

  const handleStartMockExam = async (variantFile) => {
    if (!variantFile) {
      nav.toInstructions();
      return;
    }
    const allExams = examService.getAvailableExams();
    const providerKey = examData.examProvider.toLowerCase();
    const levelKey = examData.level.toUpperCase();
    const allVariants = allExams[providerKey]?.[levelKey] || [];
    const variantConfig = allVariants.find(v => v.file === variantFile);
    if (!variantConfig || variantConfig.comingSoon) return;
    try {
      const variantExamData = await examService.loadExam(examData.examProvider, examData.level, variantConfig.id);
      navigate(`/exams/${examSlug}/instructions`, { state: { variantExamData } });
    } catch (e) {
      console.error('Failed to load exam variant:', e);
      nav.toInstructions();
    }
  };

  return (
    <ExamDetailPage
      examData={examData}
      seoH1={seoH1}
      onBack={nav.toDashboard}
      onStartPractice={(sectionId, filter) => {
        if (sectionId === 'lessons' || sectionId === 'exam_lessons') nav.toLessons();
        else if (sectionId === 'vocabulary') nav.toPracticeVocab();
        else if (sectionId === 'grammar' || sectionId === 'grammar_exercises') nav.toPracticeGrammar();
        else if (sectionId === 'grammar_lessons') nav.toPracticeGrammar();
        else nav.toPractice();
      }}
      onStartMockExam={handleStartMockExam}
      onNavigate={nav.navigate}
    />
  );
}

export function ExamSlugInstructionsPage() {
  const ctx = useOutletContext() || {};
  const { examData, examSlug } = useExamFromSlug() || {};
  const location = useLocation();
  const nav = useExamSlugNav();
  const navigate = useLocalizedNavigate();
  const variantExamData = location.state?.variantExamData;
  const effectiveExamData = variantExamData || examData;
  const uid = ctx.user?.uid || null;
  // Paper or digital: paper by default, else the last choice (this device, or
  // the profile when this device has none yet).
  const [mode, setMode] = useState(getExamModePreference);
  useEffect(() => {
    let cancelled = false;
    loadExamModePreference(uid).then((saved) => { if (saved && !cancelled) setMode(saved); });
    return () => { cancelled = true; };
  }, [uid]);
  if (!effectiveExamData) return null;
  const paperSlug = examSupportsPaper(effectiveExamData)
    ? paperSlugFor(effectiveExamData.examProvider, effectiveExamData.level)
    : null;
  const changeMode = (next) => { setMode(next); setExamModePreference(next, uid); };
  return (
    <ExamInstructionsPage
      examData={effectiveExamData}
      paperSlug={paperSlug}
      mode={mode}
      onModeChange={changeMode}
      onBeginExam={() => navigate(`/exams/${examSlug}/${paperSlug && mode === 'paper' ? 'paper' : 'simulator'}`, { state: { variantExamData } })}
      onBack={nav.toExam}
    />
  );
}

/**
 * The paper exam (booklet + answer sheet) for the paper chosen on the exam's
 * page. Finishing hands the answers to the same results page as the digital
 * simulator, which saves them to the history (marked examMode: 'paper'), runs
 * the AI writing correction and applies the free-plan limit.
 */
export function ExamSlugPaperPage() {
  const { examData, examSlug } = useExamFromSlug() || {};
  const location = useLocation();
  const nav = useExamSlugNav();
  const navigate = useLocalizedNavigate();
  const ctx = useOutletContext() || {};
  const variantExamData = location.state?.variantExamData;
  const exam = variantExamData || examData;
  const slug = exam ? paperSlugFor(exam.examProvider, exam.level) : null;
  const entry = slug ? PAPER_LEVELS[slug] : null;
  const [levelConfig, setLevelConfig] = useState(null);

  useEffect(() => {
    if (!entry) return undefined;
    let cancelled = false;
    entry.load().then((mod) => { if (!cancelled) setLevelConfig(mod.default); });
    return () => { cancelled = true; };
  }, [entry]);

  if (!exam) return null;
  // No paper version (or paper mode not released): the digital exam instead.
  if (!examSupportsPaper(exam) || !entry) {
    return <Navigate to={`/${location.pathname.split('/')[1]}/exams/${examSlug}/simulator`} replace state={{ variantExamData }} />;
  }

  // Position among the sittable papers (the cover's and sheet's version number, LGM-…-01).
  const sittable = examPapers(exam.examProvider, exam.level).filter((p) => !p.comingSoon);
  const paperIndex = Math.max(0, sittable.findIndex((p) => p.file === exam.examFileName));
  const listed = sittable[paperIndex]?.file === exam.examFileName ? sittable[paperIndex] : null;
  const paper = listed ? { ...listed, index: paperIndex } : {
    index: 0,
    id: String(exam.examFileName || exam.examId || slug).replace(/\.json$/, ''),
    name: exam.variantName || '',
    file: exam.examFileName,
  };
  const levelKey = String(exam.level).toUpperCase();

  const handleFinished = (finalAnswers, meta = {}) => {
    const results = examService.calculateFinalScore(exam, finalAnswers);
    navigate(`/exams/${examSlug}/results`, {
      state: {
        results: {
          ...results,
          userAnswers: finalAnswers,
          flaggedQuestions: [],
          timeSpent: meta.timeSpent || results.timeSpent || 0,
          sectionTimes: results.sectionTimes || {},
          examMode: 'paper',
        },
        examData: exam,
      },
    });
  };

  const loading = (
    <div className="flex items-center justify-center min-h-screen bg-[#525659]">
      <LogamiLogoSpinner size="hero" decorative={false} />
    </div>
  );
  if (!levelConfig) return loading;

  return (
    <Suspense fallback={loading}>
      <TelcPaperSitting
        key={paper.id}
        level={levelConfig}
        exam={exam}
        paper={paper}
        title={`${entry.label} · Übungstest ${paper.name}`}
        shortTitle={`Übungstest ${paper.name}`}
        audioPath={storageService.generateAudioPath(exam.examProvider, levelKey, 'listening', paper.file, paper.name)}
        onExit={nav.toExam}
        onFinished={handleFinished}
        uid={ctx.user?.uid || null}
      />
    </Suspense>
  );
}

export function ExamSlugSimulatorPage() {
  const { lang } = useParams();
  const ctx = useOutletContext() || {};
  const { user } = ctx;
  const { examData, examSlug } = useExamFromSlug() || {};
  const location = useLocation();
  const nav = useExamSlugNav();
  const navigate = useLocalizedNavigate();
  const variantExamData = location.state?.variantExamData;
  const effectiveExamData = variantExamData || examData;
  useEffect(() => {
    if (examSlug && lang) document.title = getExamTitle(examSlug, lang, 'simulator');
  }, [examSlug, lang]);
  if (!effectiveExamData) return null;
  const seoH1 = examSlug && lang ? getExamH1(examSlug, lang, 'simulator') : '';
  const handleFinishExam = (finalAnswers, flaggedQuestions, timeSpent = 0, sectionTimes = {}) => {
    const results = examService.calculateFinalScore(effectiveExamData, finalAnswers);
    const resultsWithTime = {
      ...results,
      userAnswers: finalAnswers,
      flaggedQuestions,
      timeSpent: timeSpent || results.timeSpent || 0,
      sectionTimes: Object.keys(sectionTimes).length > 0 ? sectionTimes : (results.sectionTimes || {}),
    };
    // Pass the paper that was sat. Without it the results page falls back to the
    // default paper (the free one, listed first), so a Berlin attempt was reviewed,
    // re-scored and saved against Düsseldorf's questions and answer key.
    navigate(`/exams/${examSlug}/results`, { state: { results: resultsWithTime, examData: effectiveExamData } });
  };
  return (
    <ExamSimulatorPage
      user={user}
      examData={effectiveExamData}
      onFinishExam={handleFinishExam}
      onBack={nav.toExam}
      seoH1={seoH1}
    />
  );
}

export function ExamSlugResultsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const resultsFromState = location.state?.results;
    const ctx = useOutletContext() || {};
  const { examData } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  const [loadedResult, setLoadedResult] = useState(null);
  const [loadingResult, setLoadingResult] = useState(false);

  // When no results in state (e.g. direct visit, refresh, or redirect that didn't preserve state), load latest from Firebase.
  // Re-run when auth becomes available (e.g. after hard refresh) so we fetch the result once the user is ready.
  useEffect(() => {
    if (resultsFromState || !examData?.examId || !auth.currentUser?.uid) {
      if (!auth.currentUser?.uid) setLoadedResult(null);
      return;
    }
    let cancelled = false;
    setLoadingResult(true);
    enhancedUserService
      .getLatestExamResult(auth.currentUser.uid, examData.examId)
      .then((result) => {
        if (!cancelled) setLoadedResult(result);
      })
      .catch((err) => console.error('Failed to load latest exam result:', err))
      .finally(() => { if (!cancelled) setLoadingResult(false); });
    return () => { cancelled = true; };
  }, [resultsFromState, examData?.examId, auth.currentUser?.uid]);

  const results = resultsFromState ?? loadedResult;

  if (!examData) return null;
  if (loadingResult && !results) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-gray-500">{t('examResults.loading', { defaultValue: 'Loading results…' })}</p>
      </div>
    );
  }
  return (
    <ExamResultsPage
      examData={examData}
      results={results}
      onBack={nav.toDashboard}
      subscriptionStatus={ctx.subscriptionStatus}
    />
  );
}

export function ExamSlugRetryPage() {
  const ctx = useOutletContext() || {};
  const { setId } = useParams();
  const nav = useExamSlugNav();
  const status = ctx.subscriptionStatus;
  // Same reading of "free" as the results page.
  const isFree = !!status && (status.isFree === true || (status.isActive !== true
    && ['free', 'expired', 'inactive', 'cancelled', 'canceled'].includes(String(status.status || '').toLowerCase())));
  return <RetryPage uid={auth.currentUser?.uid} setId={setId} isFree={isFree} onBack={nav.toDashboard} />;
}

export function ExamSlugPracticePage() {
  const ctx = useOutletContext() || {};
  const { examData } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <PracticePage
      examData={examData}
      user={ctx.user}
      personalizedPlan={ctx.personalizedPlan}
      onBack={nav.toDashboard}
    />
  );
}

export function ExamSlugVocabularyPage() {
  const { examData } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <VocabularyPage
      onBack={nav.toPractice}
      userLevel={examData.level || 'B1'}
    />
  );
}

export function ExamSlugGrammarPage() {
  const { examData } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <GrammarPage
      onBack={nav.toPractice}
      userLevel={examData.level || 'B1'}
    />
  );
}

export function ExamSlugRedemittelPage() {
  const { examData } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <RedemittelPage
      onBack={nav.toPractice}
      userLevel={examData.level || 'B1'}
      examData={examData}
    />
  );
}

export function ExamSlugExamSectionPractice() {
  const { examData } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <ExamSectionPractice
      examData={examData}
      onBack={nav.toPractice}
    />
  );
}

export function ExamSlugLesenTrainingPage() {
  const { examData } = useExamFromSlug() || {};
  const ctxFromParent = useOutletContext() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <LesenTrainingPage
      examData={examData}
      onBack={nav.toPractice}
      subscriptionStatus={ctxFromParent.subscriptionStatus}
      isLoadingSubscription={ctxFromParent.isLoadingSubscription}
    />
  );
}

export function ExamSlugHorenTrainingPage() {
  const { examData } = useExamFromSlug() || {};
  const ctxFromParent = useOutletContext() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <HorenTrainingPage
      examData={examData}
      onBack={nav.toPractice}
      subscriptionStatus={ctxFromParent.subscriptionStatus}
      isLoadingSubscription={ctxFromParent.isLoadingSubscription}
    />
  );
}

export function ExamSlugSchreibenTrainingPage() {
  const { examData } = useExamFromSlug() || {};
  const ctxFromParent = useOutletContext() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <SchreibenTrainingPage
      examData={examData}
      onBack={nav.toPractice}
      subscriptionStatus={ctxFromParent.subscriptionStatus}
      isLoadingSubscription={ctxFromParent.isLoadingSubscription}
    />
  );
}

export function ExamSlugLessonsPage() {
  const { lang } = useParams();
  const ctx = useOutletContext() || {};
  const { examData, examSlug } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  useEffect(() => {
    if (examSlug && lang) document.title = getLessonTitle(examSlug, lang);
  }, [examSlug, lang]);
  if (!examData) return null;
  const seoH1 = examSlug && lang ? getLessonH1(examSlug, lang) : '';
  return (
    <StudyPlanPage
      examProvider={examData.examProvider}
      examLevel={examData.level}
      seoH1={seoH1}
      examSlug={examSlug}
    />
  );
}

export function ExamSlugLessonsLevelPage() {
  const ctx = useOutletContext() || {};
  const { examData } = useExamFromSlug() || {};
  const nav = useExamSlugNav();
  if (!examData) return null;
  return (
    <GrammarLearningContainer
      user={ctx.user}
      isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
      onBack={nav.toPracticeGrammar}
      userLevel={examData.level || 'B1'}
    />
  );
}
